import { useStore } from 'hooks/useStore';
import { optionType } from 'Interfaces/FactsInterfaces/drugInterfaces';
import { useEffect, useState } from 'react';
import { futureNdcDeactivatationRequestPayload } from 'Interfaces/FactsInterfaces/ndcInterfaces';
import { factScreenServices } from 'services/factsScreenServices';
import { FactsScreenToastMessages } from 'data/toastMessages/facts';
import { toast } from 'react-toastify';

const dropDownDetailsOptions: optionType[] = [
  {
    code: 'ppssc',
    name: 'PPS Strategy & Reason',
  },
  {
    code: 'segmentation',
    name: 'Financial Segmentation',
  },
  {
    code: 'dpStrategy',
    name: 'Demand Planning',
  },
];

interface historyInterfaceForPastAndFuture {
  id: number;
  value: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

export const useNdcHistoryTable = () => {
  const {
    FactsStore: {
      ndcHistoryData,
      isNdcHistoryLoading,
      toggleNdcStrategyModalVisibility,
      deleteParticularIndexFutureNDC,
    },
  } = useStore();
  const [selectedOption, setSelectedOption] = useState<optionType | null>(null);
  const [tableData, setTableData] = useState<{
    past: historyInterfaceForPastAndFuture[];
    future: historyInterfaceForPastAndFuture[];
  }>({ past: [], future: [] });
  const [deletingIndexes, setDeletingIndexes] = useState<Set<number>>(new Set());

  const dropDownUpdate = (option: optionType) => {
    if (!ndcHistoryData) return;
    setSelectedOption(option);
    const tempTableData: {
      past: historyInterfaceForPastAndFuture[];
      future: historyInterfaceForPastAndFuture[];
    } = { past: [], future: [] };

    if (option.code === 'pps') {
      ndcHistoryData.PPS_History.past.forEach(row => {
        tempTableData.past.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'ppssc') {
      ndcHistoryData.PPS_History.past.forEach(row => {
        tempTableData.past.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'ppsssc') {
      ndcHistoryData.PPS_History.past.forEach(row => {
        tempTableData.past.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'segmentation') {
      ndcHistoryData.Segmentation.past.forEach(row => {
        tempTableData.past.push({
          id: row.ComponentID,
          reason: '',
          value: row.ReportingSegmentID.ReportingSegmentName,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'dpStrategy') {
      ndcHistoryData.dp_strategy.past.forEach(row => {
        tempTableData.past.push({
          id: row.ComponentID,
          reason: '',
          value: row.ReportingSegmentID.ReportingSegmentName,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    }

    if (option.code === 'pps') {
      ndcHistoryData.PPS_History.future.forEach(row => {
        tempTableData.future.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'ppssc') {
      ndcHistoryData.PPS_History.future.forEach(row => {
        tempTableData.future.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'ppsssc') {
      ndcHistoryData.PPS_History.future.forEach(row => {
        tempTableData.future.push({
          id: row.ID,
          reason: row.PricingStrategyReasonId.Reason,
          value: row.PricingStrategyId.PricingStrategyDescription,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'segmentation') {
      ndcHistoryData.Segmentation.future.forEach(row => {
        tempTableData.future.push({
          id: row.ComponentID,
          reason: '',
          value: row.ReportingSegmentID.ReportingSegmentName,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    } else if (option.code === 'dpStrategy') {
      ndcHistoryData.dp_strategy.future.forEach(row => {
        tempTableData.future.push({
          id: row.ComponentID,
          reason: '',
          value: row.ReportingSegmentID.ReportingSegmentName,
          startDate: row.StartDate,
          endDate: row.EndDate,
        });
      });
    }
    setTableData(tempTableData);
  };

  const handleDeleteNdcHistory = async (id: number) => {
    try {
      const tempDeletingIndexes = [...deletingIndexes];
      const tmepIndexes = new Set<number>([...tempDeletingIndexes]);
      tmepIndexes.add(id);
      setDeletingIndexes(tmepIndexes);
      const payload: futureNdcDeactivatationRequestPayload = {
        id: id,
        field: selectedOption?.code as string,
      };
      await factScreenServices.deactivateFutureNdc(payload);

      // delete the NDC future value from the history section
      if (selectedOption?.code === 'pps') {
        deleteParticularIndexFutureNDC(id, 'PPS');
      } else if (selectedOption?.code === 'ppssc') {
        deleteParticularIndexFutureNDC(id, 'PPS');
      } else if (selectedOption?.code === 'segmentation') {
        deleteParticularIndexFutureNDC(id, 'Segmentation');
      } else if (selectedOption?.code === 'dpStrategy') {
        deleteParticularIndexFutureNDC(id, 'DPStrategy');
      }

      toast.success(FactsScreenToastMessages.NDC_HISTORY_DELETE_MESSAGE);
      if (tempDeletingIndexes.length === 1) toggleNdcStrategyModalVisibility();
    } catch (_err) {
      console.log('Error occured while deleting the future tms');
    } finally {
      const tempDeletingIndexes = [...deletingIndexes];
      const tmepIndexes = new Set<number>([...tempDeletingIndexes]);
      tmepIndexes.delete(id);
      setDeletingIndexes(tmepIndexes);
    }
  };

  const checkIfCurrentRowIsBeingDelete = (marketShareId: number) => {
    if (deletingIndexes.has(marketShareId)) return true;
    return false;
  };

  const checkIfDeleteLoading = () => {
    return deletingIndexes.size ? true : false;
  };

  useEffect(() => {
    if (selectedOption) dropDownUpdate(selectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when ndcHistoryData changes
  }, [ndcHistoryData]);

  return {
    isNdcHistoryLoading,
    selectedOption,
    dropDownUpdate,
    tableData,
    dropDownDetailsOptions,
    handleDeleteNdcHistory,
    checkIfCurrentRowIsBeingDelete,
    checkIfDeleteLoading,
  };
};
